import React from "react";
import "./common.css";
import Select from "react-select";

interface Props {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  currentCount: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  menuPlacement?: "auto" | "top" | "bottom";
  pageSizeOptions?: number[];
}

// Generate pagination range with ellipsis
const getPaginationRange = (
  total: number,
  current: number
): (number | string)[] => {
  const range: (number | string)[] = [];

  if (total <= 5) {
    for (let i = 1; i <= total; i++) range.push(i);
  } else {
    range.push(1);

    if (current > 3) range.push("...");

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    if (current < total - 2) range.push("...");

    range.push(total);
  }

  return range;
};

const Pagination: React.FC<Props> = ({
  totalPages,
  currentPage,
  onPageChange,
  currentCount,
  pageSize,
  onPageSizeChange,
  menuPlacement,
  pageSizeOptions = [10, 20, 30],
}) => {
  if (totalPages < 1) return null;

  const paginationRange = getPaginationRange(totalPages, currentPage);

  return (
    <div className="pagination d-flex flex-column flex-lg-row gap-2 align-items-center justify-content-between mt-3">
      {/* Page info */}
      <div className="pagination-dropdown-grp mb-2 mb-md-0">
        <div className="pagination-dropdown">
          <p className="mb-0" style={{marginTop:"2px"}}>
          Show
          </p>
          <Select
            className="dropdown ms-0 mx-xs-2"
            classNamePrefix="react-select"
            value={{ value: pageSize, label: `${pageSize}` }}
            onChange={(selected: any) =>
              onPageSizeChange(Number(selected.value))
            }
            menuPlacement={menuPlacement ?? "auto"} 
            menuPosition="absolute"
            isSearchable={false}
            options={pageSizeOptions.map((size) => ({
              value: size,
              label: `${size}`,
            }))}
            styles={{
              control: (base: any) => ({
                ...base,
                minWidth: "76px",
                width: "76px",
                fontSize: "12px",
                borderRadius:'4px'
              }),
              menu: (base: any) => ({
                ...base, 
                zIndex: 1000,
              }),
                 menuList: (base:any) => ({
      ...base,
    }),
     option: (base: any, state: any) => ({
                    ...base,
                    backgroundColor: 'transparent',        
                    color: 'inherit',                        
                    ':active': {
                     backgroundColor: 'transparent',        
                     },
                  }),
            }}
          />
        </div>
        <p className="mb-0 text-center text-md-start" style={{marginTop:"2px"}}>
          Showing {currentCount} items on page {currentPage} of {totalPages}
        </p>
      </div>

      {/* Pagination buttons */}
      <div className="pagination-btn-grp">
        {/* Jump to first */}
        <button
          type="button"
          className="btn btn-outline-secondary button-secondary me-1 pag-btn"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >
       &laquo;
        </button>

        {/* Previous */}
        <button
          type="button"
          className="btn btn-outline-secondary button-secondary me-1 pag-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
         &lsaquo;
        </button>

        {paginationRange.map((page, index) =>
          page === "..." ? (
            <button
              key={index}
              type="button"
              className="btn btn-outline-secondary button-secondary me-1"
              disabled
            >
              ...
            </button>
          ) : (
            <button
              key={index}
              type="button"
              style={currentPage === page ? { borderLeft: "none" } : {}}
              className={`btn me-1 ${
                currentPage === page
                  ? "btn-primary button-primary"
                  : "btn-outline-secondary button-secondary pag-btn"
              }`}
              onClick={() => onPageChange(Number(page))}
            >
              {page}
            </button>
          )
        )}

        {/* Next */}
        <button
          type="button"
          className="btn btn-outline-secondary button-secondary me-1 pag-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &rsaquo;
        </button>

        {/* Jump to last */}
        <button
          type="button"
          className="btn btn-outline-secondary button-secondary pag-btn"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
           &raquo; 
        </button>
      </div>
    </div>
  );
};

export default Pagination;
